import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import {
  ExternalDataService,
  ScrapCollection,
  Zone,
} from '../../../services/externalDataService.service';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-scrap-collection-detail',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './scrap-collection-detail.component.html',
  styleUrl: './scrap-collection-detail.component.css',
})
export class ScrapCollectionDetailComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  collectionId: string = '';
  collection: ScrapCollection | null = null;
  zones: Zone[] = [];

  loading = false;
  errorMessage = '';

  // Toggle invalid state
  togglingInvalid = false;
  toggleError = '';
  showConfirmDialog = false;

  // Convert to Cash state
  convertingToCash = false;
  convertError = '';
  convertSuccess = '';
  showConvertToCashDialog = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private externalDataService: ExternalDataService,
  ) {}

  ngOnInit(): void {
    this.collectionId = this.route.snapshot.paramMap.get('id') || '';

    if (!this.collectionId) {
      this.errorMessage = 'Invalid collection ID';
      return;
    }

    this.loadZones();
    this.loadCollection();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadZones(): void {
    this.externalDataService
      .getAllZones()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (zones) => {
          this.zones = zones;
        },
        error: (error) => {
          console.error('Error loading zones:', error);
        },
      });
  }

  loadCollection(): void {
    this.loading = true;
    this.errorMessage = '';

    this.externalDataService
      .getScrapCollectionById(this.collectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (collection) => {
          this.collection = collection;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading scrap collection:', error);

          if (error.error?.detail) {
            this.errorMessage = error.error.detail;
          } else if (error.error?.message) {
            this.errorMessage = error.error.message;
          } else if (error.status === 401) {
            this.errorMessage = 'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.errorMessage =
              'Access denied. You do not have permission to view this collection.';
          } else if (error.status === 404) {
            this.errorMessage = 'Scrap collection not found.';
          } else if (error.status >= 500) {
            this.errorMessage = 'Server error. Please try again later.';
          } else if (error.status === 0 || error.status === undefined) {
            this.errorMessage =
              'Network error. Please check your connection and try again.';
          } else {
            this.errorMessage =
              'Failed to load scrap collection. Please try again.';
          }

          this.loading = false;
        },
      });
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatCurrency(amount: number): string {
    return `₹${amount.toFixed(2)}`;
  }

  getGoogleMapsUrl(): string {
    if (
      !this.collection?.location?.latitude ||
      !this.collection?.location?.longitude
    ) {
      return '';
    }

    const lat = this.collection.location.latitude;
    const lng = this.collection.location.longitude;
    return `https://www.google.com/maps?q=${lat},${lng}`;
  }

  getZoneName(zoneId: string): string {
    const zone = this.zones.find((z) => z.id === zoneId);
    return zone ? zone.name : 'N/A';
  }

  getPaymentMethodName(paymentMethod?: number | string): string {
    if (paymentMethod === undefined || paymentMethod === null) {
      return 'N/A';
    }
    const pm = typeof paymentMethod === 'string' ? parseInt(paymentMethod, 10) : paymentMethod;
    switch (pm) {
      case 1:
        return 'Cash';
      case 2:
        return 'Bank Transfer';
      case 3:
        return 'UPI';
      case 4:
        return 'Card';
      case 5:
        return 'Voucher';
      default:
        return typeof paymentMethod === 'string' ? paymentMethod : 'Unknown';
    }
  }

  isCollectionValid(): boolean {
    // If isValid is not set or is true, collection is valid
    return this.collection?.isValid !== false;
  }

  canMarkInvalid(): boolean {
    return this.isCollectionValid() && !this.togglingInvalid;
  }

  openConfirmDialog(): void {
    if (!this.canMarkInvalid()) {
      return;
    }
    this.showConfirmDialog = true;
    this.toggleError = '';
  }

  closeConfirmDialog(): void {
    this.showConfirmDialog = false;
    this.toggleError = '';
  }

  confirmMarkInvalid(): void {
    if (!this.collection || !this.canMarkInvalid()) {
      return;
    }

    this.togglingInvalid = true;
    this.toggleError = '';

    this.externalDataService
      .setScrapCollectionInvalid(this.collection.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Update local state
          if (this.collection) {
            this.collection.isValid = false;
          }
          this.togglingInvalid = false;
          this.closeConfirmDialog();
        },
        error: (error) => {
          console.error('Error setting scrap collection as invalid:', error);
          this.togglingInvalid = false;

          if (error.error?.detail) {
            this.toggleError = error.error.detail;
          } else if (error.error?.message) {
            this.toggleError = error.error.message;
          } else if (error.status === 401) {
            this.toggleError = 'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.toggleError =
              'Access denied. You do not have permission to perform this action.';
          } else if (error.status === 404) {
            this.toggleError = 'Scrap collection not found.';
          } else if (error.status >= 500) {
            this.toggleError = 'Server error. Please try again later.';
          } else {
            this.toggleError =
              'Failed to update scrap collection status. Please try again.';
          }
        },
      });
  }

  isVoucherPayment(): boolean {
    if (!this.collection) return false;
    const pm = this.collection.paymentMethod;
    if (
      pm === 5 ||
      pm === '5' ||
      pm === 2 ||
      pm === '2' ||
      (typeof pm === 'string' && pm.toLowerCase().includes('voucher'))
    ) {
      return true;
    }
    if (
      (this.collection.voucherCommission && this.collection.voucherCommission > 0) ||
      (this.collection.agentVoucherCommission && this.collection.agentVoucherCommission > 0)
    ) {
      return true;
    }
    return false;
  }

  canConvertToCash(): boolean {
    return this.isCollectionValid() && this.isVoucherPayment() && !this.convertingToCash;
  }

  openConvertToCashDialog(): void {
    if (!this.canConvertToCash()) {
      return;
    }
    this.showConvertToCashDialog = true;
    this.convertError = '';
    this.convertSuccess = '';
  }

  closeConvertToCashDialog(): void {
    this.showConvertToCashDialog = false;
    this.convertError = '';
  }

  confirmConvertToCash(): void {
    if (!this.collection || !this.canConvertToCash()) {
      return;
    }

    this.convertingToCash = true;
    this.convertError = '';
    this.convertSuccess = '';

    this.externalDataService
      .convertScrapCollectionToCash(this.collection.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedCollection) => {
          this.collection = updatedCollection;
          this.convertingToCash = false;
          this.convertSuccess = 'Payment method successfully converted to Cash / UPI.';
          this.closeConvertToCashDialog();
        },
        error: (error) => {
          console.error('Error converting collection to cash:', error);
          this.convertingToCash = false;

          if (error.error?.detail) {
            this.convertError = error.error.detail;
          } else if (error.error?.message) {
            this.convertError = error.error.message;
          } else if (error.status === 401) {
            this.convertError = 'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.convertError =
              'Access denied. You do not have permission to perform this action.';
          } else if (error.status === 404) {
            this.convertError = 'Scrap collection not found.';
          } else if (error.status >= 500) {
            this.convertError = 'Server error. Please try again later.';
          } else {
            this.convertError =
              'Failed to convert scrap collection payment method to cash. Please try again.';
          }
        },
      });
  }

  downloadCSV(): void {
    if (!this.collection) {
      return;
    }

    // Prepare CSV content
    const lines: string[] = [];

    // Title
    lines.push('Scrap Collection Report');
    lines.push('');

    // Collection Information
    lines.push('Collection Information');
    lines.push(
      `Collection ID,${this.collection.collectionId || this.collection.id}`,
    );
    lines.push(`Appointment ID,${this.collection.appointmentId || 'N/A'}`);
    lines.push(`Collection Date,${this.formatDate(this.collection.date)}`);
    lines.push(`Zone,${this.getZoneName(this.collection.geoZoneId)}`);
    lines.push(`Status,${this.isCollectionValid() ? 'Valid' : 'Invalid'}`);
    lines.push('');

    // Customer Information
    lines.push('Customer Information');
    lines.push(`Customer Name,${this.collection.customerName || 'N/A'}`);
    lines.push(`Phone Number,${this.collection.customerPhoneNumber || 'N/A'}`);
    lines.push(
      `Address,"${(this.collection.address || 'N/A').replace(/"/g, '""')}"`,
    );
    lines.push(`Location,${this.collection.locationName || 'N/A'}`);
    lines.push('');

    // Agent Information
    if (this.collection.agentDetails) {
      lines.push('Agent Information');
      lines.push(`Agent Name,${this.collection.agentDetails.name || 'N/A'}`);
      lines.push(
        `Phone Number,${this.collection.agentDetails.phoneNumber || 'N/A'}`,
      );
      lines.push(
        `Vehicle Number,${this.collection.agentDetails.vehicleNumber || 'N/A'}`,
      );
      lines.push(
        `Rating,${this.collection.agentDetails.agentRating ? this.collection.agentDetails.agentRating + ' / 5' : 'N/A'}`,
      );
      lines.push('');
    }

    // Financial Summary
    lines.push('Financial Summary');
    lines.push(`Total Items,${this.collection.totalItems}`);
    lines.push(`Total Scrap Price,${this.collection.totalScrapPrice}`);
    lines.push(`Total Non-Scrap Price,${this.collection.totalNonScrapPrice}`);
    lines.push(`Collection Charges,${this.collection.collectionCharges}`);
    lines.push(`Platform Charges,${this.collection.platformCharges}`);
    lines.push(`Grand Total,${this.collection.grandTotal}`);
    lines.push('');

    // Items Collected
    if (
      this.collection.collectionDetails &&
      this.collection.collectionDetails.length > 0
    ) {
      lines.push('Items Collected');
      lines.push(
        'Item Name,Quantity,Unit,Deducted,Net Quantity,Market Price,Total Price',
      );

      this.collection.collectionDetails.forEach((item) => {
        const netQuantity = item.quantity - (item.deductedQuantity || 0);
        lines.push(
          [
            `"${item.scrapItemName.replace(/"/g, '""')}"`,
            item.quantity,
            item.unit,
            item.deductedQuantity || 0,
            netQuantity,
            item.marketPrice || 'N/A',
            item.totalPrice,
          ].join(','),
        );
      });
    }

    // Create CSV content
    const csvContent = lines.join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `scrap-collection-${this.collection.collectionId || this.collection.id}-${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  goBack(): void {
    this.router.navigate(['/scrap-collections-list']);
  }
}
